import type { LucideIcon } from 'lucide-react';

export type DangerLevel = 'safe' | 'warning' | 'destructive';
export type ActionCategory =
  | 'platform-fixes'
  | 'data-operations'
  | 'queue-operations'
  | 'system-maintenance';

export interface ActionField {
  name: string;
  label: string;
  type: 'store-select' | 'platform-select' | 'queue-select' | 'date' | 'text' | 'select';
  required?: boolean;
  placeholder?: string;
  // Options for the generic 'select' field type.
  options?: { value: string; label: string }[];
}

export interface ActionDefinition {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  icon: LucideIcon;
  category: ActionCategory;
  dangerLevel: DangerLevel;
  endpoint: string;
  fields: ActionField[];
  buildPayload?: (values: Record<string, string>) => Record<string, unknown>;
}

export interface ActionExecution {
  id: string;
  actionId: string;
  actionName: string;
  timestamp: string;
  inputs: Record<string, string>;
  status: 'running' | 'success' | 'error';
  result?: unknown;
  error?: string;
  durationMs?: number;
}

export interface CategoryMeta {
  id: ActionCategory;
  label: string;
  icon: LucideIcon;
}

export const DANGER_STYLES: Record<DangerLevel, { badge: string; button: string; label: string }> =
  {
    safe: {
      badge: 'bg-green-50 text-green-700 border-green-200',
      button: 'bg-gray-900 text-white hover:bg-gray-800',
      label: 'Safe',
    },
    warning: {
      badge: 'bg-amber-50 text-amber-700 border-amber-200',
      button: 'bg-amber-600 text-white hover:bg-amber-700',
      label: 'Warning',
    },
    destructive: {
      badge: 'bg-red-50 text-red-700 border-red-200',
      button: 'bg-red-600 text-white hover:bg-red-700',
      label: 'Destructive',
    },
  };

export const PLATFORM_OPTIONS = [
  { value: 'shopify', label: 'Shopify' },
  { value: 'meta', label: 'Meta Ads' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'tiktok_business', label: 'TikTok Business' },
  { value: 'tiktok_shop', label: 'TikTok Shop' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'klaviyo', label: 'Klaviyo' },
  { value: 'amazon_ads', label: 'Amazon Ads' },
  { value: 'amazon_seller', label: 'Amazon Seller' },
  { value: 'ga4', label: 'Google Analytics' },
];

export const QUEUE_OPTIONS = [
  { value: 'backfill', label: 'Backfill' },
  { value: 'daily-pull', label: 'Daily Pull' },
  { value: 'gap-backfill', label: 'Gap Backfill' },
  { value: 'pull-ad-metrics', label: 'Pull Ad Metrics' },
  { value: 'pull-orders', label: 'Pull Orders' },
];
